import { useState } from 'react';
import { X, Send, Phone, Mail, MessageCircle, Loader2, Check } from 'lucide-react';
import whatsappIcon from '../images/icons/icon-whatsapp.webp';

const QuickContact = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    const payload = {
      ...formData,
      enquiry: 'quick-contact',
      company: '',
      phone: '',
    };

    try {
      // Try Vercel API first
      let response = await fetch('/api/send-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      // If Vercel fails, fallback to Render
      if (!response.ok) {
        response = await fetch('https://molomo-email-service.onrender.com/api/send-email', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
      }

      const data = await response.json();

      if (data.success) {
        setIsSubmitted(true);
        setTimeout(() => {
          setIsSubmitted(false);
          setFormData({ name: '', email: '', message: '' });
          setIsOpen(false);
        }, 3000);
      } else {
        setError(data.error || 'Failed to send message');
      }
    } catch (err) {
      setError('Failed to send message. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) {
    return (
      <div className="fixed bottom-6 left-6 z-[100]">
        <button
          onClick={() => setIsOpen(true)}
          className="group flex items-center gap-3 bg-charcoal text-white px-5 py-3 rounded-full shadow-lg hover:bg-charcoal/90 transition-all duration-300 hover:scale-105"
        >
          <div className="w-8 h-8 rounded-full bg-lime flex items-center justify-center">
            <MessageCircle className="w-4 h-4 text-charcoal" />
          </div>
          <span className="font-medium text-sm">Message us quickly</span>
        </button>
      </div>
    );
  }

  return (
    <div className="fixed bottom-6 left-6 z-[100] w-80 md:w-96">
      <div className="bg-white rounded-2xl shadow-2xl overflow-hidden border border-charcoal/10">
        {/* Header */}
        <div className="bg-charcoal px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-lime flex items-center justify-center">
              <MessageCircle className="w-5 h-5 text-charcoal" />
            </div>
            <div>
              <h4 className="font-display font-bold text-white text-sm">Message us quickly</h4>
              <p className="text-white/60 text-xs">We typically reply within hours</p>
            </div>
          </div>
          <button
            onClick={() => setIsOpen(false)}
            aria-label="Close quick contact"
            className="text-white/60 hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-5">
          {isSubmitted ? (
            <div className="text-center py-6">
              <div className="w-12 h-12 rounded-full bg-lime/20 flex items-center justify-center mx-auto mb-3">
                <Check className="w-6 h-6 text-lime" />
              </div>
              <h5 className="font-display font-bold text-charcoal mb-1">Message sent!</h5>
              <p className="text-charcoal/60 text-sm">We'll get back to you soon.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-xs">
                  {error}
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  placeholder="Your name"
                  className="w-full h-10 px-3 rounded-lg border border-charcoal/20 bg-white text-charcoal text-sm focus:outline-none focus:ring-2 focus:ring-lime"
                  style={{ WebkitBoxShadow: '0 0 0 30px white inset' }}
                />
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  placeholder="Your email"
                  className="w-full h-10 px-3 rounded-lg border border-charcoal/20 bg-white text-charcoal text-sm focus:outline-none focus:ring-2 focus:ring-lime"
                  style={{ WebkitBoxShadow: '0 0 0 30px white inset' }}
                />
              </div>

              <textarea
                name="message"
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                required
                rows={3}
                placeholder="How can we help you?"
                className="w-full px-3 py-2 rounded-lg border border-charcoal/20 bg-white text-charcoal text-sm focus:outline-none focus:ring-2 focus:ring-lime resize-none"
                style={{ WebkitBoxShadow: '0 0 0 30px white inset' }}
              />

              <button
                type="submit"
                disabled={isLoading}
                className="w-full h-10 bg-lime text-charcoal hover:bg-lime/90 font-medium rounded-lg flex items-center justify-center gap-2 disabled:opacity-50 text-sm"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    Send Message
                  </>
                )}
              </button>

              {/* Quick Actions */}
              <div className="flex items-center justify-center gap-4 pt-2 border-t border-charcoal/10">
                <a
                  href="tel:+27829713299"
                  className="flex items-center gap-1.5 text-charcoal/60 hover:text-charcoal text-xs transition-colors"
                >
                  <Phone className="w-3.5 h-3.5" />
                  Call
                </a>
                <a
                  href="mailto:nhlapolindiwe4@gmail.com"
                  className="flex items-center gap-1.5 text-charcoal/60 hover:text-charcoal text-xs transition-colors"
                >
                  <Mail className="w-3.5 h-3.5" />
                  Email
                </a>
                <a
                  href="https://api.whatsapp.com/send?phone=27829713299"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1.5 text-charcoal/60 hover:text-charcoal text-xs "
                >
                  <img src={whatsappIcon} alt="WhatsApp" className="w-3.5 h-3.5 bg-charcoal/60" />
                  WhatsApp
                </a>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuickContact;
